import { createAsyncThunk } from "@reduxjs/toolkit";

import { setUser } from "../../actions/user";
import { getSignedUrl, uploadToS3 } from "../../apilist/fileUploadApi";
import { get } from "../../utils/barrels/lodash";
import { reloadUser } from "../../utils/functions";

import type { UploadSingleFileParams } from "./types";

const SUCCESS_STATUS = [200, 201, 204];

/**
 * Upload a single file: get signed URL, then upload to S3
 *
 * @param params - File, uniqueName, ext, urlPath
 * @returns { ok, key, s3Url, fields } on success, { ok: false } on failure
 */
export const uploadSingleFile = createAsyncThunk(
	"fileUpload/uploadSingleFile",
	async (params: UploadSingleFileParams, thunkAPI) => {
		const { file, uniqueName, ext, urlPath } = params;

		try {
			const res = await getSignedUrl(uniqueName, ext, urlPath);

			if (res.status !== 200 && res.status !== 201) {
				const err = new Error(
					(res.data as { msg?: string })?.msg || "Failed to get signed URL"
				);
				throw err;
			}

			const { url: s3Url, fields } = res.data as {
				url: string;
				fields: Record<string, string>;
			};

			const uploadRes = await uploadToS3(file, s3Url, fields);

			if (SUCCESS_STATUS.includes(uploadRes.status)) {
				return { ok: true as const, key: fields.key, s3Url, fields };
			}

			const uploadErr = new Error(
				(uploadRes.data as { msg?: string })?.msg || "Upload failed"
			);
			throw uploadErr;
		} catch (err: unknown) {
			const errResponse = get(err, "response.status", false);
			if (typeof errResponse === "number" && errResponse === 401) {
				reloadUser();
				thunkAPI.dispatch(
					setUser({
						reAuth: true,
						showReAuthModal: true,
						requestType: "post",
					})
				);
				window.location.reload();
			}

			const errorMessage =
				err instanceof Error ? err.message : "Failed to upload file";
			return thunkAPI.rejectWithValue(errorMessage);
		}
	}
);
